import { type ReactNode, useEffect } from 'react'
import { useEscape } from '@core/shortcuts'

interface MobileDrawerProps {
  side: 'left' | 'right'
  title: string
  onClose: () => void
  children: ReactNode
}

// Slide-in overlay used on phones to host the existing Sidebar /
// RightSidebar without their desktop docking. The `!`-prefixed
// utilities neutralise the inline width / min-width / max-width the
// asides set for the desktop layout so they fill the drawer instead.
// (`!` generates `!important`, which overrides the inline style.)
//
// O selector tem de ser `> div > aside`: o `<aside>` não é filho direto
// deste contentor, vive dentro da caixa de conteúdo por baixo do
// cabeçalho do gaveto. Com o antigo `[&>aside]` nada casava, e daí vinham
// duas queixas reais no telemóvel: a barra lateral mantinha a largura
// fixa de desktop (380px numa gaveta de 322px, cortada à direita) e,
// sobretudo, mantinha altura automática, pelo que o `flex-1 overflow-auto`
// lá dentro nunca ficava limitado e as propriedades não faziam scroll.
// O `!h-full` amarra a altura à da gaveta para o scroll interno voltar.
export default function MobileDrawer({
  side,
  title,
  onClose,
  children,
}: MobileDrawerProps) {
  // Com o despachante de atalhos montado, é este registo que fecha a
  // gaveta; o listener abaixo cobre o caso em que ele não está.
  useEscape(true, onClose)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="absolute inset-0 z-40 flex" role="dialog" aria-label={title}>
      {/* Backdrop: tap to close. */}
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-black/50"
      />
      <div
        className={`relative z-10 flex h-full w-[86vw] max-w-[360px] flex-col bg-[var(--color-panel)] shadow-2xl ${
          side === 'right' ? 'ml-auto' : ''
        } [&>div>aside]:!flex [&>div>aside]:!h-full [&>div>aside]:!w-full [&>div>aside]:!min-w-0 [&>div>aside]:!max-w-none [&>div>aside]:!border-0`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] px-3 py-2">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-7 w-7 items-center justify-center rounded text-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          >
            ×
          </button>
        </div>
        {/* `flex` para o <aside> lá dentro esticar à altura da caixa: é
            essa altura definida que dá scroll aos painéis internos. */}
        <div className="flex min-h-0 flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  )
}
